import { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from '@tanstack/react-router';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api, ApiError } from '../lib/api';
import {
  formatCAD, formatDate, formatClosing, fitPct, verdictMeta, statusMeta,
  type MatchStatus, type Verdict,
} from '../lib/format';
import { pushToast } from '../hooks/useToasts';
import { isInsideEditable } from '../hooks/useGlobalShortcuts';
import { MatchImpactPreview } from '../components/MatchImpactPreview';

// ── API shape ────────────────────────────────────────────────────────────

interface MatchListItem {
  id: string;
  partner_profile_id: string;
  grant_program_id: string;
  fit_score: number;
  eligibility_verdict: Verdict;
  status: MatchStatus;
  status_updated_at: number | null;
  surfaced_at: number;
  grant_title: string;
  grant_source_url: string;
  funder_id: string;
  funder_name: string;
  funder_level: string;
  closes_at: number | null;
  intake_type: string | null;
  profile_name: string;
  profile_slug: string;
  // Derived in the UI from the source feed; partner-level rows omit project_*.
  match_level: 'partner' | 'project';
  project_id?: string;
  project_name?: string;
}

// Project-level match shape from GET /project-matches. Mostly the same as
// MatchListItem with project_id + project_name + partner_name where
// MatchListItem uses profile_name.
interface ProjectMatchListItem {
  id: string;
  partner_profile_id: string;
  grant_program_id: string;
  project_id: string;
  fit_score: number;
  eligibility_verdict: Verdict;
  status: MatchStatus;
  status_updated_at: number | null;
  surfaced_at: number;
  grant_title: string;
  grant_source_url: string;
  funder_id: string;
  funder_name: string;
  funder_level: string;
  closes_at: number | null;
  intake_type: string | null;
  partner_name: string;
  partner_slug: string;
  project_name: string;
}

interface Reason { point: string; citation: { field: string; quote: string; source_url: string } }

interface MatchDetail extends MatchListItem {
  grant_description: string | null;
  eligible_applicants: string[];
  eligible_activities: string[];
  geographic_scope: string[];
  sectors: string[];
  funding_min_cad: number | null;
  funding_max_cad: number | null;
  match_required: boolean;
  match_ratio: number | null;
  application_complexity: string | null;
  opens_at: number | null;
  decision_by: number | null;
  source_quotes: Record<string, string>;
  indigenous_eligible: boolean;
  indigenous_required: boolean;
  notes: string | null;
  assigned_to: string | null;
  reasoning_json: {
    eligibility_verdict: Verdict;
    fit_score: number;
    reasons: Reason[];
    concerns: string[];
  } | null;
}

interface Profile { id: string; slug: string; name: string }

// ── Page ─────────────────────────────────────────────────────────────────

export interface TriageProps {
  showShortcuts: boolean;
  setShowShortcuts: (v: boolean) => void;
}

export default function TriagePage({ showShortcuts, setShowShortcuts }: TriageProps) {
  const navigate = useNavigate();
  const qc = useQueryClient();

  // Filters.
  const [profileFilter, setProfileFilter] = useState<string>('all');
  const [statusFilter, setStatusFilter] = useState<MatchStatus | 'all' | 'open'>('open');
  const [levelFilter, setLevelFilter] = useState<'all' | 'partner' | 'project'>('all');
  const [filterText, setFilterText] = useState('');

  const profiles = useQuery({
    queryKey: ['profiles'],
    queryFn: () => api.get<Profile[]>('/profiles'),
  });

  const matchesQ = useQuery({
    queryKey: ['matches', profileFilter, statusFilter],
    queryFn: () => {
      const params = new URLSearchParams();
      if (profileFilter !== 'all') params.set('profile', profileFilter);
      // statusFilter 'open' means show everything except dismissed/applied.
      if (statusFilter !== 'all' && statusFilter !== 'open') params.set('status', statusFilter);
      return api.get<MatchListItem[]>(`/matches?${params.toString()}`);
    },
  });

  // Project-level matches feed. Same filters where applicable.
  const projectMatchesQ = useQuery({
    queryKey: ['project-matches', profileFilter, statusFilter],
    queryFn: () => {
      const params = new URLSearchParams();
      if (profileFilter !== 'all') params.set('partner', profileFilter);
      if (statusFilter !== 'all' && statusFilter !== 'open') params.set('status', statusFilter);
      return api.get<ProjectMatchListItem[]>(`/project-matches?${params.toString()}`);
    },
  });

  // Unified feed: partner-level matches stamped match_level='partner', plus
  // project-level matches mapped into MatchListItem shape with match_level=
  // 'project' and project_id/project_name set. Both sorted by status priority
  // then fit_score (server already orders within each feed; we re-sort across).
  const matches = useMemo<MatchListItem[]>(() => {
    const partnerList: MatchListItem[] = (matchesQ.data?.data ?? []).map((m) => ({
      ...m, match_level: 'partner' as const,
    }));
    const projectList: MatchListItem[] = (projectMatchesQ.data?.data ?? []).map((pm) => ({
      id: pm.id,
      partner_profile_id: pm.partner_profile_id,
      grant_program_id: pm.grant_program_id,
      fit_score: pm.fit_score,
      eligibility_verdict: pm.eligibility_verdict,
      status: pm.status,
      status_updated_at: pm.status_updated_at,
      surfaced_at: pm.surfaced_at,
      grant_title: pm.grant_title,
      grant_source_url: pm.grant_source_url,
      funder_id: pm.funder_id,
      funder_name: pm.funder_name,
      funder_level: pm.funder_level,
      closes_at: pm.closes_at,
      intake_type: pm.intake_type,
      profile_name: pm.partner_name,
      profile_slug: pm.partner_slug,
      match_level: 'project',
      project_id: pm.project_id,
      project_name: pm.project_name,
    }));
    let filtered = [...partnerList, ...projectList];
    if (statusFilter === 'open') filtered = filtered.filter((m) => m.status !== 'dismissed' && m.status !== 'applied');
    if (levelFilter !== 'all') filtered = filtered.filter((m) => m.match_level === levelFilter);
    if (filterText.trim()) {
      const needle = filterText.trim().toLowerCase();
      filtered = filtered.filter((m) =>
        m.grant_title.toLowerCase().includes(needle) ||
        m.funder_name.toLowerCase().includes(needle) ||
        (m.project_name?.toLowerCase().includes(needle) ?? false),
      );
    }
    // Re-sort merged feed: status priority then fit_score desc.
    const statusOrder: Record<MatchStatus, number> = {
      new: 0, watching: 1, pursuing: 2, escalated: 3, dismissed: 4, applied: 5,
    };
    filtered.sort((a, b) => {
      const so = statusOrder[a.status] - statusOrder[b.status];
      return so !== 0 ? so : b.fit_score - a.fit_score;
    });
    return filtered;
  }, [matchesQ.data, projectMatchesQ.data, statusFilter, levelFilter, filterText]);

  const [selectedIdx, setSelectedIdx] = useState(0);
  useEffect(() => { setSelectedIdx(0); }, [profileFilter, statusFilter, filterText]);
  const selected = matches[selectedIdx];

  const listRef = useRef<HTMLDivElement | null>(null);

  // ── Keyboard nav scoped to the triage page ─────────────────────────
  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      if (isInsideEditable(e.target)) return;
      if (showShortcuts) return; // overlay open
      if (palette.open) return;
      switch (e.key) {
        case 'j':
        case 'ArrowDown':
          e.preventDefault();
          setSelectedIdx((i) => Math.min(matches.length - 1, i + 1));
          return;
        case 'k':
        case 'ArrowUp':
          e.preventDefault();
          setSelectedIdx((i) => Math.max(0, i - 1));
          return;
        case 'Enter':
          // Auto-selected; Enter just keeps focus consistent and scrolls into view.
          listRef.current?.querySelector<HTMLElement>(`[data-idx="${selectedIdx}"]`)?.scrollIntoView({ block: 'nearest' });
          return;
        case 'w': case 'p': case 'n': {
          if (!selected) return;
          e.preventDefault();
          const status: MatchStatus = e.key === 'w' ? 'watching' : e.key === 'p' ? 'pursuing' : 'dismissed';
          mutateStatus.mutate({ id: selected.id, status, level: selected.match_level });
          return;
        }
        case 'e':
          if (!selected) return;
          e.preventDefault();
          mutateStatus.mutate({ id: selected.id, status: 'escalated', level: selected.match_level });
          return;
        case 'a':
          if (!selected) return;
          e.preventDefault();
          setShowPromote(true);
          return;
      }
    }
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [matches, selected, selectedIdx, showShortcuts]);

  // Detail fetch. Routes to /matches/:id or /project-matches/:id based on
  // the selected row's match_level — both endpoints return compatible
  // detail shapes (project-match adds project_name etc.; partner-match
  // omits them).
  const detailQ = useQuery({
    queryKey: ['match-detail', selected?.match_level, selected?.id],
    queryFn: () => {
      if (!selected) throw new Error('no selection');
      const path = selected.match_level === 'project'
        ? `/project-matches/${selected.id}`
        : `/matches/${selected.id}`;
      return api.get<MatchDetail>(path);
    },
    enabled: !!selected,
  });

  // Status mutation — optimistic. Routes to the right endpoint based on
  // match_level. Reads/writes both ['matches', …] and ['project-matches', …]
  // query caches so the optimistic update lands on whichever feed the row
  // came from.
  const mutateStatus = useMutation({
    mutationFn: ({ id, status, level }: { id: string; status: MatchStatus; level: 'partner' | 'project' }) => {
      const path = level === 'project' ? `/project-matches/${id}` : `/matches/${id}`;
      return api.patch(path, { status });
    },
    onMutate: async ({ id, status, level }) => {
      const key = level === 'project'
        ? ['project-matches', profileFilter, statusFilter]
        : ['matches', profileFilter, statusFilter];
      await qc.cancelQueries({ queryKey: key });
      const prev = qc.getQueryData<{ data: Array<{ id: string }> }>(key);
      if (prev) {
        const next = { ...prev, data: prev.data.map((m) => m.id === id ? { ...m, status } : m) };
        qc.setQueryData(key, next);
      }
      return { prev, key };
    },
    onError: (err, _vars, ctx) => {
      if (ctx?.prev && ctx?.key) qc.setQueryData(ctx.key, ctx.prev);
      pushToast(err instanceof ApiError ? err.message : 'Status change failed', 'error');
    },
    onSuccess: (_data, vars) => {
      pushToast(`Marked ${statusMeta[vars.status].label}`, 'success', 1800);
    },
  });

  // ── Promote-to-application inline form ─────────────────────────────
  const [showPromote, setShowPromote] = useState(false);
  useEffect(() => { setShowPromote(false); }, [selected?.id]);

  // ── Palette ─────────────────────────────────────────────────────────
  const palette = usePalette({ matches, onPick: (i) => setSelectedIdx(i) });

  // ── Render ──────────────────────────────────────────────────────────
  if (matchesQ.error instanceof ApiError && matchesQ.error.status === 401) {
    navigate({ to: '/login' });
    return null;
  }

  return (
    <div className="grid h-full grid-cols-[35%_1fr] border-t border-neutral-200">
      <aside className="border-r border-neutral-200 flex flex-col min-h-0">
        {/* Tab strip — quick filters for the most common slices. Maps to
            statusFilter; the dropdown below remains for precise tuning. */}
        <div role="tablist" aria-label="Match status tabs" className="px-3 pt-3 flex items-center gap-1 text-xs">
          {([
            { value: 'open', label: 'Inbox' },
            { value: 'watching', label: 'Watching' },
            { value: 'pursuing', label: 'Pursued' },
            { value: 'dismissed', label: 'Dismissed' },
            { value: 'all', label: 'All' },
          ] as const).map((tab) => {
            const active = statusFilter === tab.value;
            return (
              <button
                key={tab.value}
                role="tab"
                aria-selected={active}
                type="button"
                onClick={() => setStatusFilter(tab.value)}
                className={
                  'px-2 py-1 rounded-md border ' +
                  (active
                    ? 'bg-accent text-white border-accent'
                    : 'border-neutral-300 text-neutral-700 hover:bg-neutral-50')
                }
              >{tab.label}</button>
            );
          })}
        </div>
        <div className="p-3 border-b border-neutral-200 flex flex-wrap gap-2 items-center text-sm">
          <select
            aria-label="Partner filter"
            className="border border-neutral-300 rounded-md px-2 py-1 text-xs"
            value={profileFilter}
            onChange={(e) => setProfileFilter(e.target.value)}
          >
            <option value="all">All partners</option>
            {(profiles.data?.data ?? []).map((p) => (
              <option key={p.id} value={p.id}>{p.name}</option>
            ))}
          </select>
          <select
            aria-label="Status filter"
            className="border border-neutral-300 rounded-md px-2 py-1 text-xs"
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value as MatchStatus | 'all' | 'open')}
          >
            <option value="open">Open</option>
            <option value="all">All</option>
            <option value="new">New</option>
            <option value="watching">Watching</option>
            <option value="pursuing">Pursuing</option>
            <option value="escalated">Escalated</option>
            <option value="dismissed">Dismissed</option>
            <option value="applied">Applied</option>
          </select>
          <select
            aria-label="Match level filter"
            className="border border-neutral-300 rounded-md px-2 py-1 text-xs"
            value={levelFilter}
            onChange={(e) => setLevelFilter(e.target.value as 'all' | 'partner' | 'project')}
          >
            <option value="all">Partner + project</option>
            <option value="partner">Partner-level only</option>
            <option value="project">Project-level only</option>
          </select>
          <input
            data-shortcut-target="filter"
            placeholder="Filter (/)"
            aria-label="Search match list"
            value={filterText}
            onChange={(e) => setFilterText(e.target.value)}
            className="flex-1 border border-neutral-300 rounded-md px-2 py-1 text-xs"
          />
        </div>
        {/* Flat listbox — buttons are direct children of [role=listbox] so
            aria-required-children/parent rules are satisfied. */}
        <div ref={listRef} role="listbox" aria-label="Matches" className="flex-1 overflow-y-auto">
          {(matchesQ.isLoading || projectMatchesQ.isLoading) && (
            <div className="p-3 text-sm text-neutral-600">Loading matches — a moment.</div>
          )}
          {!matchesQ.isLoading && !projectMatchesQ.isLoading && matches.length === 0 && (
            <TriageEmptyState
              hasAnyData={(matchesQ.data?.data.length ?? 0) + (projectMatchesQ.data?.data.length ?? 0) > 0}
              statusFilter={statusFilter}
              onClearStatusFilter={() => setStatusFilter('all')}
            />
          )}
          {matches.map((m, i) => (
            <button
              key={`${m.match_level}:${m.id}`}
              data-idx={i}
              type="button"
              role="option"
              aria-selected={i === selectedIdx}
              onClick={() => setSelectedIdx(i)}
              className={
                'w-full text-left px-3 py-2 border-b border-neutral-100 ' +
                'min-h-16 flex flex-col gap-0.5 ' +
                (i === selectedIdx
                  ? 'bg-accent/5 border-l-2 border-l-accent'
                  : 'hover:bg-neutral-50')
              }
            >
              <div className="flex items-center gap-2 text-sm">
                <span aria-hidden="true" className={m.status === 'new' ? 'text-accent' : 'text-neutral-400'}>
                  {m.status === 'new' ? '●' : '○'}
                </span>
                <span className="font-medium truncate flex-1">{m.grant_title}</span>
              </div>
              <div className="text-xs text-neutral-700 truncate">
                {m.funder_name} · {formatClosing(m.closes_at)}
              </div>
              <div className="flex items-center gap-1 text-[11px] flex-wrap">
                <Chip cls="bg-neutral-200 text-neutral-900">{fitPct(m.fit_score)}</Chip>
                <Chip cls={verdictMeta[m.eligibility_verdict].cls}>{verdictMeta[m.eligibility_verdict].label}</Chip>
                <Chip cls={statusMeta[m.status].cls}>{statusMeta[m.status].label}</Chip>
                <Chip cls={m.match_level === 'project' ? 'bg-amber-100 text-amber-900' : 'bg-neutral-100 text-neutral-700'}>
                  {m.match_level === 'project'
                    ? `for ${m.project_name}`
                    : `for ${m.profile_name}`}
                </Chip>
              </div>
            </button>
          ))}
        </div>
      </aside>

      {selected ? (
        <Detail
          item={selected}
          detail={detailQ.data?.data}
          isLoading={detailQ.isLoading}
          showPromote={showPromote}
          setShowPromote={setShowPromote}
          onStatus={(status) => mutateStatus.mutate({ id: selected.id, status, level: selected.match_level })}
          onPromoteCreated={() => {
            qc.invalidateQueries({ queryKey: ['matches'] });
            qc.invalidateQueries({ queryKey: ['project-matches'] });
          }}
        />
      ) : (
        <div className="p-6 text-sm text-neutral-600">Select a match to review it.</div>
      )}

      {palette.node}
      {showShortcuts && <ShortcutOverlay onClose={() => setShowShortcuts(false)} />}
    </div>
  );
}

// ── Empty state ──────────────────────────────────────────────────────────

function TriageEmptyState({
  hasAnyData, statusFilter, onClearStatusFilter,
}: {
  hasAnyData: boolean;
  statusFilter: MatchStatus | 'all' | 'open';
  onClearStatusFilter: () => void;
}) {
  if (!hasAnyData) {
    return (
      <div className="p-5 text-sm text-neutral-700 leading-relaxed">
        <p className="font-medium text-neutral-900">You're caught up.</p>
        <p className="mt-2">New matches arrive overnight when the cron runs. Want to look now? Trigger any source under{' '}
          <a href="/admin/sources" className="text-accent underline">Sources → Run now</a>, or verify any pending grants in{' '}
          <a href="/admin/verify" className="text-accent underline">Verification</a>.
        </p>
      </div>
    );
  }
  return (
    <div className="p-4 text-sm text-neutral-700">
      No matches in this filter.
      {statusFilter === 'open' && (
        <button
          type="button"
          className="block mt-2 text-accent underline"
          onClick={onClearStatusFilter}
        >
          Show all statuses
        </button>
      )}
    </div>
  );
}

// ── Detail pane ──────────────────────────────────────────────────────────

interface DetailProps {
  item: MatchListItem;
  detail?: MatchDetail;
  isLoading: boolean;
  showPromote: boolean;
  setShowPromote: (v: boolean) => void;
  onStatus: (s: MatchStatus) => void;
  onPromoteCreated: () => void;
}

function Detail({ item, detail, isLoading, showPromote, setShowPromote, onStatus, onPromoteCreated }: DetailProps) {
  const d = detail;
  return (
    <section className="flex flex-col min-h-0">
      <div className="overflow-y-auto flex-1">
        <header className="px-5 pt-4 pb-3 border-b border-neutral-200">
          <div className="flex items-start gap-3">
            <h1 className="text-lg font-semibold text-neutral-900 leading-tight flex-1">{item.grant_title}</h1>
            <div className="flex items-center gap-1 text-[11px] shrink-0">
              <Chip cls="bg-neutral-100 text-neutral-700">{fitPct(item.fit_score)}</Chip>
              <Chip cls={verdictMeta[item.eligibility_verdict].cls}>{verdictMeta[item.eligibility_verdict].label}</Chip>
              <Chip cls={statusMeta[item.status].cls}>{statusMeta[item.status].label}</Chip>
            </div>
          </div>
          <p className="mt-1 text-xs text-neutral-600">
            {item.funder_name} <span className="mx-1">·</span>
            <span className="uppercase tracking-wide">{item.funder_level}</span>
            <span className="mx-1">·</span>
            {formatClosing(item.closes_at)}
            <span className="mx-1">·</span>
            <a className="underline" href={item.grant_source_url} target="_blank" rel="noreferrer noopener">funder page ↗</a>
          </p>
          <p className="mt-1 text-xs text-neutral-500">For: {item.profile_name}</p>
        </header>

        {isLoading || !d ? (
          <div className="p-6 text-sm text-neutral-600">Loading match detail — a moment.</div>
        ) : (
          <div className="px-5 py-4 space-y-5">
            <KeyFacts d={d} />
            <Reasons reasons={d.reasoning_json?.reasons ?? []} />
            <Concerns concerns={d.reasoning_json?.concerns ?? []} />
            {showPromote && (
              <PromoteForm
                match={d}
                onCancel={() => setShowPromote(false)}
                onCreated={() => { setShowPromote(false); onPromoteCreated(); }}
              />
            )}
          </div>
        )}
      </div>

      <ActionBar
        status={item.status}
        onAction={(action) => {
          if (action === 'a') setShowPromote(true);
          else if (action === 'w') onStatus('watching');
          else if (action === 'p') onStatus('pursuing');
          else if (action === 'n') onStatus('dismissed');
          else if (action === 'e') onStatus('escalated');
        }}
      />
    </section>
  );
}

function KeyFacts({ d }: { d: MatchDetail }) {
  const rows: Array<[string, string, string | null]> = [
    ['Funding range', `${formatCAD(d.funding_min_cad)} – ${formatCAD(d.funding_max_cad)}`, d.source_quotes.funding_max_cad ?? d.source_quotes.funding_min_cad ?? null],
    ['Match required', d.match_required ? `Yes${d.match_ratio ? ` (${d.match_ratio}:1)` : ''}` : 'No', d.source_quotes.match_required ?? null],
    ['Complexity', d.application_complexity ?? '—', null],
    ['Intake', d.intake_type ?? '—', d.source_quotes.intake_type ?? null],
    ['Opens', formatDate(d.opens_at), d.source_quotes.opens_at ?? null],
    ['Closes', formatDate(d.closes_at), d.source_quotes.closes_at ?? null],
    ['Decision by', formatDate(d.decision_by), d.source_quotes.decision_by ?? null],
  ];
  return (
    <section>
      <h2 className="text-xs uppercase tracking-wide text-neutral-600 mb-2">Key facts</h2>
      <table className="w-full text-sm">
        <tbody>
          {rows.map(([label, value, quote]) => (
            <tr key={label} className="border-b border-neutral-100 last:border-0">
              <th scope="row" className="text-left font-normal text-neutral-600 py-1.5 pr-3 w-40 align-top">{label}</th>
              <td className="py-1.5 align-top">
                <span>{value}</span>
                {quote && (
                  <details className="inline-block ml-2 text-xs text-neutral-500">
                    <summary className="cursor-pointer hover:text-neutral-700">[source]</summary>
                    <blockquote className="mt-1 font-mono whitespace-pre-wrap border-l-2 border-neutral-300 pl-2 text-neutral-700 max-w-xl">{quote}</blockquote>
                  </details>
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </section>
  );
}

function Reasons({ reasons }: { reasons: Reason[] }) {
  if (reasons.length === 0) return (
    <section>
      <h2 className="text-xs uppercase tracking-wide text-neutral-600 mb-2">Why this matches</h2>
      <p className="text-sm text-neutral-500">No grounded reasons were produced by the verdict step.</p>
    </section>
  );
  return (
    <section>
      <h2 className="text-xs uppercase tracking-wide text-neutral-600 mb-2">Why this matches</h2>
      <ul className="space-y-2">
        {reasons.map((r, i) => (
          <li key={i} className="text-sm">
            <p className="text-neutral-900">{r.point}</p>
            <details className="text-xs text-neutral-500 mt-1">
              <summary className="cursor-pointer hover:text-neutral-700">Show source quote</summary>
              <blockquote className="mt-1 font-mono whitespace-pre-wrap border-l-2 border-neutral-300 pl-2 text-neutral-700">
                {r.citation.quote}
              </blockquote>
              <a href={r.citation.source_url} target="_blank" rel="noreferrer noopener" className="text-accent underline">
                Source ↗
              </a>
            </details>
          </li>
        ))}
      </ul>
    </section>
  );
}

function Concerns({ concerns }: { concerns: string[] }) {
  if (concerns.length === 0) return null;
  return (
    <section>
      <h2 className="text-xs uppercase tracking-wide text-neutral-600 mb-2">Concerns / open questions</h2>
      <ul className="list-disc pl-5 space-y-1 text-sm text-neutral-800">
        {concerns.map((c, i) => <li key={i}>{c}</li>)}
      </ul>
    </section>
  );
}

// ── Promote-to-application ───────────────────────────────────────────────

interface ProjectOption {
  id: string;
  partner_profile_id: string;
  name: string;
  status: string;
}

function PromoteForm({ match, onCancel, onCreated }: { match: MatchDetail; onCancel: () => void; onCreated: () => void }) {
  const qc = useQueryClient();
  const [ask, setAsk] = useState<number>(match.funding_max_cad ?? 0);
  const defaultMatch = match.match_required ? Math.round((match.funding_min_cad ?? match.funding_max_cad ?? 0) * (match.match_ratio ?? 1)) : 0;
  const [commit, setCommit] = useState<number>(defaultMatch);
  // OPTIONAL attach-to-project. Empty string = "No project". Defaults
  // to the project_id if this row came from a project-level match (so
  // Lisa doesn't have to re-select), otherwise empty.
  const [projectId, setProjectId] = useState<string>(match.project_id ?? '');
  const [showCreateProject, setShowCreateProject] = useState(false);
  const [showPreview, setShowPreview] = useState(false);

  // Projects under the matched partner — surfaced in the dropdown.
  const projects = useQuery({
    queryKey: ['projects-for-promote', match.partner_profile_id],
    queryFn: () =>
      api.get<ProjectOption[]>(`/projects?partner_id=${encodeURIComponent(match.partner_profile_id)}`),
  });

  const mutation = useMutation({
    mutationFn: (matchCommittedCad: number) => api.post('/applications', {
      // If this row IS a project-level match, project_match_id carries
      // implicit project_id; otherwise we pass project_id explicitly when
      // Lisa attached a project.
      ...(match.match_level === 'project'
        ? { project_match_id: match.id }
        : { match_id: match.id, ...(projectId ? { project_id: projectId } : {}) }),
      funding_ask_cad: ask,
      match_committed_cad: matchCommittedCad,
    }),
    onSuccess: () => {
      const projName = projectId
        ? projects.data?.data.find((p) => p.id === projectId)?.name
        : undefined;
      pushToast(
        projName ? `Application created · attached to ${projName}` : 'Application created — press G A to view',
        'success',
      );
      onCreated();
    },
    onError: (err) => pushToast(err instanceof ApiError ? err.message : 'Create failed', 'error'),
  });

  // Whether the attach-to-project dropdown should appear. Hide it for
  // project-level rows since the attach is implicit.
  const showAttachDropdown = match.match_level !== 'project';

  return (
    <form
      onSubmit={(e) => { e.preventDefault(); setShowPreview(true); }}
      onKeyDown={(e) => {
        if (e.key === 'Escape' && !showPreview && !showCreateProject) { e.preventDefault(); onCancel(); }
      }}
      className="border border-accent rounded-md p-3 bg-accent/5"
    >
      <h2 className="text-xs uppercase tracking-wide text-neutral-800 mb-2">Promote to application</h2>
      <div className="grid grid-cols-2 gap-3 text-sm">
        <label className="block">
          <span className="block text-xs text-neutral-700 mb-1">Funding ask (CAD)</span>
          <input
            autoFocus
            type="number"
            min={0}
            value={ask}
            onChange={(e) => setAsk(Number(e.target.value))}
            className="w-full border border-neutral-300 rounded-md px-2 py-1.5"
          />
        </label>
        <label className="block">
          <span className="block text-xs text-neutral-700 mb-1">Match commitment (CAD)</span>
          <input
            type="number"
            min={0}
            value={commit}
            onChange={(e) => setCommit(Number(e.target.value))}
            className="w-full border border-neutral-300 rounded-md px-2 py-1.5"
          />
        </label>
      </div>

      {showAttachDropdown && (
        <div className="mt-3 text-sm">
          <label className="block">
            <span className="block text-xs text-neutral-700 mb-1">
              Attach to project <span className="text-neutral-500 font-normal">(optional)</span>
            </span>
            <select
              value={projectId}
              onChange={(e) => setProjectId(e.target.value)}
              className="w-full border border-neutral-300 rounded-md px-2 py-1.5 text-xs"
            >
              <option value="">No project (track in Applications only)</option>
              {(projects.data?.data ?? []).filter((p) => p.status !== 'cancelled').map((p) => (
                <option key={p.id} value={p.id}>{p.name}</option>
              ))}
            </select>
          </label>
          <button
            type="button"
            onClick={() => setShowCreateProject(true)}
            className="mt-1 text-[11px] text-accent underline"
          >
            Create new project →
          </button>
        </div>
      )}
      {match.match_level === 'project' && match.project_name && (
        <p className="mt-3 text-[11px] text-amber-900 bg-amber-50 border border-amber-200 rounded px-2 py-1">
          This match is scoped to <span className="font-medium">{match.project_name}</span>. The application will inherit that project automatically.
        </p>
      )}

      <div className="flex items-center gap-2 mt-3">
        <button
          type="submit"
          disabled={mutation.isPending}
          className="px-3 py-1.5 rounded-md bg-accent text-white text-sm disabled:opacity-60 flex items-center gap-1.5"
        >
          {mutation.isPending ? 'Creating…' : 'Promote →'}
          <kbd className="font-mono text-[10px] bg-black/20 px-1 py-0.5 rounded">⏎</kbd>
        </button>
        <button
          type="button"
          onClick={onCancel}
          className="px-3 py-1.5 rounded-md border border-neutral-300 text-sm"
        >
          Cancel <kbd className="font-mono text-[10px] bg-neutral-100 px-1 rounded ml-1">Esc</kbd>
        </button>
        <span className="text-[11px] text-neutral-500 ml-1">A match-impact preview opens before commit.</span>
      </div>
      {showPreview && (
        <div className="mt-3 relative">
          <MatchImpactPreview
            grantProgramId={match.grant_program_id}
            fundingAskCad={ask}
            partnerProfileId={match.partner_profile_id}
            projectId={projectId || match.project_id || undefined}
            matchCommittedCad={commit}
            onCancel={() => setShowPreview(false)}
            onConfirm={(estimatedMatch) => mutation.mutate(estimatedMatch)}
            isConfirming={mutation.isPending}
          />
        </div>
      )}
      {showCreateProject && (
        <CreateProjectInlineModal
          partnerProfileId={match.partner_profile_id}
          partnerName={match.profile_name}
          onClose={() => setShowCreateProject(false)}
          onCreated={(newProjectId) => {
            setShowCreateProject(false);
            setProjectId(newProjectId);
            // Refresh the dropdown so the new project is selectable.
            qc.invalidateQueries({ queryKey: ['projects-for-promote', match.partner_profile_id] });
            qc.invalidateQueries({ queryKey: ['projects'] });
            pushToast('Project created — selected for this promote', 'success', 2000);
          }}
        />
      )}
    </form>
  );
}

// Thin inline modal: just name + partner pre-filled. Full project details
// can be edited from the project's detail page after creation.
function CreateProjectInlineModal({
  partnerProfileId, partnerName, onClose, onCreated,
}: {
  partnerProfileId: string;
  partnerName: string;
  onClose: () => void;
  onCreated: (newProjectId: string) => void;
}) {
  const [name, setName] = useState('');
  const inputRef = useRef<HTMLInputElement>(null);
  useEffect(() => { inputRef.current?.focus(); }, []);

  const create = useMutation({
    mutationFn: () => api.post<{ id: string; name: string; status: string }>('/projects', {
      partner_profile_id: partnerProfileId,
      name: name.trim(),
      status: 'planning',
    }),
    onSuccess: (res) => onCreated(res.data.id),
    onError: (err) => pushToast(err instanceof ApiError ? err.message : 'Create project failed', 'error'),
  });

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label="Create new project"
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/30"
      onClick={onClose}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        onKeyDown={(e) => { if (e.key === 'Escape') { e.preventDefault(); onClose(); } }}
        className="bg-white rounded-md shadow-lg w-[420px] p-4"
      >
        <h3 className="text-sm font-semibold text-neutral-900">New project</h3>
        <p className="mt-1 text-xs text-neutral-600">
          For partner <span className="font-medium text-neutral-900">{partnerName}</span>. You can add description, ask amount, and matching funds later from the project detail page.
        </p>
        <form
          className="mt-3"
          onSubmit={(e) => {
            e.preventDefault();
            if (!name.trim() || create.isPending) return;
            create.mutate();
          }}
        >
          <label className="block">
            <span className="block text-xs text-neutral-700 mb-1">Project name <span className="text-rose-700">*</span></span>
            <input
              ref={inputRef}
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Short, specific name"
              className="w-full border border-neutral-300 rounded-md px-2 py-1.5 text-sm"
            />
          </label>
          <div className="mt-3 flex items-center gap-2">
            <button
              type="submit"
              disabled={!name.trim() || create.isPending}
              className="rounded-md bg-accent text-white px-3 py-1.5 text-sm disabled:opacity-50 flex items-center gap-1.5"
            >
              {create.isPending ? 'Creating…' : 'Create'}
              <kbd className="font-mono text-[10px] bg-black/20 px-1 py-0.5 rounded">⏎</kbd>
            </button>
            <button
              type="button"
              onClick={onClose}
              className="rounded-md border border-neutral-300 px-3 py-1.5 text-sm"
            >Cancel <kbd className="font-mono text-[10px] bg-neutral-100 px-1 rounded ml-1">Esc</kbd></button>
          </div>
        </form>
      </div>
    </div>
  );
}

// ── Action bar ───────────────────────────────────────────────────────────

function ActionBar({ status, onAction }: { status: MatchStatus; onAction: (a: 'w' | 'p' | 'n' | 'e' | 'a') => void }) {
  const Btn = ({ k, label, accent }: { k: 'w' | 'p' | 'n' | 'e' | 'a'; label: string; accent?: boolean }) => (
    <button
      type="button"
      onClick={() => onAction(k)}
      className={
        'px-3 py-1.5 rounded-md text-sm flex items-center gap-2 ' +
        (accent ? 'bg-accent text-white hover:bg-accent-hover' : 'border border-neutral-300 hover:bg-neutral-50')
      }
    >
      <span>{label}</span>
      <span className="text-[10px] uppercase tracking-wide bg-black/10 px-1 rounded">{k.toUpperCase()}</span>
    </button>
  );
  // Dismissed matches collapse the action bar to a single "Restore" CTA so
  // it's clear the row is parked. Restore maps to action 'p' (Pursue) so a
  // restored match lands in the active queue.
  if (status === 'dismissed') {
    return (
      <footer className="h-16 border-t border-neutral-200 bg-white px-4 flex items-center gap-2">
        <span className="text-xs text-neutral-600">Dismissed — restoring sends it back to the inbox.</span>
        <div className="flex-1" />
        <Btn k="p" label="Restore to inbox" accent />
      </footer>
    );
  }
  return (
    <footer className="h-16 border-t border-neutral-200 bg-white px-4 flex items-center gap-2">
      <Btn k="w" label="Watch" />
      <Btn k="p" label="Pursue" />
      <Btn k="n" label="Dismiss" />
      <Btn k="e" label="Escalate" />
      <div className="flex-1" />
      <Btn k="a" label="Promote to application" accent />
      <span className="text-[11px] text-neutral-500 ml-2">current: {statusMeta[status].label}</span>
    </footer>
  );
}

// ── Shortcut overlay ─────────────────────────────────────────────────────

function ShortcutOverlay({ onClose }: { onClose: () => void }) {
  return (
    <div role="dialog" aria-modal="true" aria-label="Keyboard shortcuts" className="fixed inset-0 bg-black/30 flex items-center justify-center z-50" onClick={onClose}>
      <div className="bg-white rounded-md shadow-lg p-5 w-[480px]" onClick={(e) => e.stopPropagation()}>
        <h2 className="text-base font-semibold mb-3">Keyboard shortcuts</h2>
        <dl className="grid grid-cols-[100px_1fr] gap-y-1 text-sm">
          {[
            ['↑ / k', 'Previous match'],
            ['↓ / j', 'Next match'],
            ['Enter', 'Scroll selected into view'],
            ['w', 'Watching'],
            ['p', 'Pursuing'],
            ['n', 'Dismissed'],
            ['e', 'Escalate'],
            ['a', 'Promote to application'],
            ['/', 'Focus filter'],
            ['⌘K', 'Command palette'],
            ['?', 'This overlay'],
            ['Esc', 'Close overlay / form'],
            ['g t', 'Go to Triage'],
            ['g v', 'Go to Verify queue'],
            ['Cmd+Enter', 'Submit promote form'],
          ].map(([k, v]) => (
            <div key={k} className="contents">
              <dt><kbd className="font-mono text-xs bg-neutral-100 px-1.5 py-0.5 rounded border border-neutral-200">{k}</kbd></dt>
              <dd className="text-neutral-700">{v}</dd>
            </div>
          ))}
        </dl>
        <div className="mt-4 text-right">
          <button onClick={onClose} className="px-3 py-1 rounded-md border border-neutral-300 text-sm">Close</button>
        </div>
      </div>
    </div>
  );
}

// ── Tiny primitive ───────────────────────────────────────────────────────

function Chip({ cls, children }: { cls: string; children: React.ReactNode }) {
  return <span className={`inline-block px-1.5 py-0.5 rounded text-[11px] leading-tight ${cls}`}>{children}</span>;
}

// ── Palette (cmdk) ───────────────────────────────────────────────────────

import { Command } from 'cmdk';

function usePalette({ matches, onPick }: { matches: MatchListItem[]; onPick: (idx: number) => void }) {
  const [open, setOpen] = useState(false);
  const [value, setValue] = useState('');
  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'k') {
        e.preventDefault();
        setOpen(true);
      }
      if (e.key === 'Escape' && open) setOpen(false);
    }
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open]);

  const node = open ? (
    <div role="dialog" aria-modal="true" aria-label="Command palette" className="fixed inset-0 bg-black/30 flex items-start justify-center pt-24 z-50" onClick={() => setOpen(false)}>
      <Command
        className="bg-white rounded-md shadow-lg w-[560px] overflow-hidden"
        onClick={(e) => e.stopPropagation()}
        shouldFilter={true}
      >
        <Command.Input
          autoFocus
          value={value}
          onValueChange={setValue}
          placeholder="Search matches and actions…"
          className="w-full px-3 py-3 border-b border-neutral-200 outline-none text-sm"
        />
        <Command.List className="max-h-[60vh] overflow-y-auto">
          <Command.Empty className="px-3 py-3 text-sm text-neutral-500">No results.</Command.Empty>
          <Command.Group heading="Matches">
            {matches.slice(0, 30).map((m, idx) => (
              <Command.Item
                key={m.id}
                value={`match ${m.grant_title} ${m.funder_name} ${m.profile_name}`}
                onSelect={() => { onPick(idx); setOpen(false); }}
                className="px-3 py-2 text-sm cursor-pointer aria-selected:bg-accent/10"
              >
                <span className="font-medium">{m.grant_title}</span>
                <span className="ml-2 text-xs text-neutral-500">{m.funder_name} · {m.profile_name}</span>
              </Command.Item>
            ))}
          </Command.Group>
          <Command.Group heading="Actions">
            <Command.Item value="action go triage" onSelect={() => { setOpen(false); }} className="px-3 py-2 text-sm cursor-pointer aria-selected:bg-accent/10">
              Go to Triage <kbd className="ml-2 text-[10px] bg-neutral-100 px-1 rounded">g t</kbd>
            </Command.Item>
            <Command.Item value="action go verify" onSelect={() => { setOpen(false); window.location.assign('/admin/verify'); }} className="px-3 py-2 text-sm cursor-pointer aria-selected:bg-accent/10">
              Go to Verify queue <kbd className="ml-2 text-[10px] bg-neutral-100 px-1 rounded">g v</kbd>
            </Command.Item>
            <Command.Item value="action signout" onSelect={async () => { await api.post('/auth/logout'); window.location.assign('/login'); }} className="px-3 py-2 text-sm cursor-pointer aria-selected:bg-accent/10">
              Sign out
            </Command.Item>
          </Command.Group>
        </Command.List>
      </Command>
    </div>
  ) : null;

  return { open, node };
}
